import { HelpArticleBody } from '@/components/help/HelpArticleBody'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Separator } from '@/components/ui/separator'
import { Sheet, SheetContent } from '@/components/ui/sheet'
import { helpApi } from '@/lib/api/help.api'
import { ArrowSquareOut } from '@phosphor-icons/react'
import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'

import { HelpPlanBadge } from './HelpPlanBadge'
import { useHelpVisibility } from './useHelpVisibility'

interface HelpDrawerProps {
  helpId: string | null
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function HelpDrawer({ helpId, open, onOpenChange }: HelpDrawerProps) {
  const { isEntryVisible, flagsReady } = useHelpVisibility()
  const entryQuery = useQuery({
    queryKey: ['help-entry', helpId],
    queryFn: () => helpApi.get(helpId!),
    enabled: open && !!helpId,
  })

  // Suppress contextual help for a module the current workspace can't use.
  const entry =
    entryQuery.data && flagsReady && !isEntryVisible(entryQuery.data)
      ? undefined
      : entryQuery.data

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full p-0 sm:max-w-xl">
        <ScrollArea className="h-full">
          <div className="px-6 py-5">
            {entryQuery.isLoading ? (
              <div className="space-y-3">
                <div className="h-4 w-28 rounded bg-muted" />
                <div className="h-7 w-3/4 rounded bg-muted" />
                <div className="h-20 rounded bg-muted" />
              </div>
            ) : entry ? (
              <article>
                <div className="space-y-3">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-xs uppercase tracking-wide text-muted-foreground">
                      {entry.moduleSlug ?? 'help'} · {entry.type}
                    </span>
                    <HelpPlanBadge plans={entry.featurePlans} />
                  </div>
                  <div>
                    <h2 className="text-2xl font-semibold tracking-normal">
                      {entry.title}
                    </h2>
                    <p className="mt-2 text-sm text-muted-foreground">
                      {entry.summary}
                    </p>
                  </div>
                  <Link
                    to={`/help/articles/${encodeURIComponent(entry.id)}`}
                    onClick={() => onOpenChange(false)}
                    className="inline-flex w-fit items-center gap-2 rounded-md border border-border px-3 py-1.5 text-xs font-medium hover:bg-muted"
                  >
                    Open article
                    <ArrowSquareOut className="h-3.5 w-3.5" />
                  </Link>
                </div>

                <Separator className="my-5" />

                <HelpArticleBody
                  body={entry.body}
                  className="text-sm"
                />

                {(entry.relatedIds.length > 0 || entry.appPaths.length > 0) && (
                  <>
                    <Separator className="my-5" />
                    <div className="space-y-3 text-sm">
                      {entry.relatedIds.length > 0 && (
                        <div>
                          <div className="font-medium">Related help</div>
                          <div className="mt-1 text-muted-foreground">
                            {entry.relatedIds.join(', ')}
                          </div>
                        </div>
                      )}
                      {entry.appPaths.length > 0 && (
                        <div>
                          <div className="font-medium">App locations</div>
                          <div className="mt-1 text-muted-foreground">
                            {entry.appPaths.join(', ')}
                          </div>
                        </div>
                      )}
                    </div>
                  </>
                )}
              </article>
            ) : (
              <div className="rounded-md border border-dashed p-4 text-sm text-muted-foreground">
                Help article not found.
              </div>
            )}
          </div>
        </ScrollArea>
      </SheetContent>
    </Sheet>
  )
}
